import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createStyles, makeStyles } from '@mui/styles'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
} from '@mui/material'
import AddAlarmIcon from '@mui/icons-material/AddAlarm'
import AlarmIcon from '@mui/icons-material/Alarm'
import EmailIcon from '@mui/icons-material/Email'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/de'
import Env from '../../shared/config/envConfig'
import { setMessage } from '../../store/settings/settings.actions'
import { Severity } from '@3m5/crude-frontend/dist/shared/types/messageTypes'

dayjs.locale('de')

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 'calc(100% - 32px)',
      padding: theme.spacing(2),
      margin: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        width: '100%',
        margin: 0,
        padding: theme.spacing(1),
        borderRadius: 0,
        boxShadow: 'none',
      },
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    groupHeader: {
      backgroundColor: theme.palette.grey[200],
      fontWeight: 'bold',
    },
    areaHeader: {
      backgroundColor: theme.palette.grey[400],
      fontWeight: 'bold',
    },
    filterContainer: {
      marginBottom: theme.spacing(3),
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    filterFormControl: {
      minWidth: 200,
      flexGrow: 1,
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    mobileCard: {
      marginBottom: theme.spacing(2),
      borderLeft: `6px solid ${theme.palette.primary.main}`,
    },
    mobileLabel: {
      fontWeight: 'bold',
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
    },
    mobileValue: {
      fontSize: '0.95rem',
    },
    mobileActionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(1),
    },
    mobileSectionHeader: {
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
  }),
)

interface AdminPlanDto {
  id: string
  helperId: string
  helperName: string
  helperKennung: string | null
  helperEmail: string | null
  shirtNeeded: boolean
  isClubMember: boolean
  employmentId: string
  employmentName: string
  area: {
    displayName: string
  }
  eventDay: string
  areaName: string
  dayFrom: string | null
  dayTo: string | null
  checkinAt: string | null
  checkoutAt: string | null
}

const AdminPlan: React.FC = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const [data, setData] = useState<AdminPlanDto[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState<string>('all')
  const [selectedArea, setSelectedArea] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Manual Check-in/out State
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<AdminPlanDto | null>(null)
  const [selectedType, setSelectedType] = useState<'CHECKIN' | 'CHECKOUT'>('CHECKIN')
  const [manualTime, setManualTime] = useState<Dayjs | null>(dayjs())
  const [resendStatus, setResendStatus] = useState<Record<string, 'loading' | 'success' | 'idle'>>({})
  const [shirtLoading, setShirtLoading] = useState<Record<string, boolean>>({})

  const fetchData = async () => {
    setLoading(true)
    const restUrl = `${Env.basePath}/api/admin/plan`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Einsatz-Übersicht')
      }
      const result = await response.json()
      setData(result)
    } catch (error) {
      console.error(error)
      dispatch(setMessage({
        severity: Severity.error,
        message: 'Die Einsatz-Übersicht konnte nicht geladen werden.',
      }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleOpenDialog = (record: AdminPlanDto, field: 'checkin' | 'checkout') => {
    setSelectedRecord(record)
    setSelectedType(field === 'checkin' ? 'CHECKIN' : 'CHECKOUT')
    const existingValue = field === 'checkin' ? record.checkinAt : record.checkoutAt
    setManualTime(existingValue ? dayjs(existingValue) : dayjs())
    setDialogOpen(true)
  }

  const handleManualCheckin = async () => {
    if (!selectedRecord || !manualTime) return

    const restUrl = `${Env.basePath}/api/admin/checkin`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify({
          kennung: selectedRecord.helperKennung || '',
          datetime: manualTime.format('YYYY-MM-DDTHH:mm:ss'),
          type: selectedType,
          employmentId: selectedRecord.employmentId,
        }),
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Manueller Check-in/out fehlgeschlagen.')
      }

      dispatch(setMessage({
        severity: Severity.success,
        message: 'Check-in/out erfolgreich aktualisiert.',
      }))
      setDialogOpen(false)
      fetchData() // Refresh table
    } catch (error: any) {
      dispatch(setMessage({
        severity: Severity.error,
        message: error.message,
      }))
    }
  }

  const handleResendKennung = async (helperId: string) => {
    setResendStatus((prev) => ({ ...prev, [helperId]: 'loading' }))
    const restUrl = `${Env.basePath}/api/admin/helper/resend-kennung?helperId=${helperId}`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Versand der Kennung fehlgeschlagen.')
      }

      setResendStatus((prev) => ({ ...prev, [helperId]: 'success' }))
      dispatch(setMessage({
        severity: Severity.success,
        message: 'Kennung wurde erfolgreich erneut versendet.',
      }))
    } catch (error: any) {
      setResendStatus((prev) => ({ ...prev, [helperId]: 'idle' }))
      dispatch(setMessage({
        severity: Severity.error,
        message: error.message,
      }))
    }
  }

  const handleToggleShirt = async (helperId: string) => {
    setShirtLoading((prev) => ({ ...prev, [helperId]: true }))
    const restUrl = `${Env.basePath}/api/admin/plan/toggle-shirt?helperId=${helperId}`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    try {
      const response = await fetch(restUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })

      if (!response.ok) {
        throw new Error('Fehler beim Aktualisieren des Shirt-Status')
      }

      const newValue = await response.json()

      // Update local state for all records with this helperId
      setData((prevData) =>
        prevData.map((item) =>
          item.helperId === helperId ? { ...item, shirtNeeded: newValue } : item,
        ),
      )
    } catch (error: any) {
      console.error(error)
      dispatch(setMessage({
        severity: Severity.error,
        message: 'Der Shirt-Status konnte nicht aktualisiert werden.',
      }))
    } finally {
      setShirtLoading((prev) => ({ ...prev, [helperId]: false }))
    }
  }

  const formatDateTime = (dateTimeStr: string | null) => {
    if (!dateTimeStr) return '-'
    return new Date(dateTimeStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-'
    // Assuming timeStr is "HH:mm:ss" or similar
    return timeStr.substring(0, 5)
  }

  const uniqueDays = Array.from(new Set(data.map((item) => item.eventDay))).sort()
  const uniqueAreas = Array.from(new Set(data.map((item) => item.area.displayName))).sort()

  const filteredData = data.filter((item) => {
    const matchesDay = selectedDay === 'all' || item.eventDay === selectedDay
    const matchesArea = selectedArea === 'all' || item.area.displayName === selectedArea
    const matchesSearch = item.helperName.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesDay && matchesArea && matchesSearch
  })

  const groupedData = filteredData.reduce((acc, curr) => {
    const areaKey = curr.area.displayName
    const employmentKey = `${curr.employmentName} (${curr.eventDay})`

    if (!acc[areaKey]) {
      acc[areaKey] = {}
    }
    if (!acc[areaKey][employmentKey]) {
      acc[areaKey][employmentKey] = []
    }
    acc[areaKey][employmentKey].push(curr)
    return acc
  }, {} as Record<string, Record<string, AdminPlanDto[]>>)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
      <Paper className={classes.root}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' gutterBottom>
          <Typography variant='h5'>
            Einsatz-Übersicht (Check-in / Check-out)
          </Typography>
          <IconButton onClick={fetchData} title='Daten neu laden' disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {!loading && (
          <Box className={classes.filterContainer}>
            <TextField
              label='Name suchen'
              variant='outlined'
              size='small'
              className={classes.filterFormControl}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <FormControl className={classes.filterFormControl} variant='outlined' size='small'>
              <InputLabel id='day-filter-label'>Tag filtern</InputLabel>
              <Select
                labelId='day-filter-label'
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value as string)}
                label='Tag filtern'
              >
                <MenuItem value='all'>Alle Tage</MenuItem>
                {uniqueDays.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              size='small'
              className={classes.filterFormControl}
              options={['Alle Bereiche', ...uniqueAreas]}
              value={selectedArea === 'all' ? 'Alle Bereiche' : selectedArea}
              onChange={(_, newValue) => {
                setSelectedArea(newValue === 'Alle Bereiche' || !newValue ? 'all' : newValue)
              }}
              renderInput={(params) => (
                <TextField {...params} label='Bereich filtern' variant='outlined' />
              )}
            />
          </Box>
        )}

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Box>
            {Object.keys(groupedData).map((areaKey) => (
              <Box key={areaKey} sx={{ mb: 4 }}>
                <Box className={`${classes.areaHeader} ${classes.mobileSectionHeader}`}>
                  <Typography variant='h6'>
                    Bereich: {areaKey}
                  </Typography>
                </Box>
                {Object.keys(groupedData[areaKey]).map((employmentKey) => (
                  <Box key={employmentKey} sx={{ mb: 2, pl: isMobile ? 0 : 2 }}>
                    <Box className={`${classes.groupHeader} ${classes.mobileSectionHeader}`}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                        {employmentKey}
                      </Typography>
                    </Box>
                    {groupedData[areaKey][employmentKey].map((row) => (
                      <Card key={row.id} className={classes.mobileCard}>
                        <CardContent>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant='h6' color='primary'>{row.helperName}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Verein</Typography>
                              <Typography className={classes.mobileValue}>
                                {row.isClubMember ? <CheckIcon color='success' fontSize='small' /> : '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Shirt</Typography>
                              <Checkbox
                                checked={row.shirtNeeded}
                                onChange={() => handleToggleShirt(row.helperId)}
                                disabled={shirtLoading[row.helperId]}
                                size='small'
                                sx={{ p: 0 }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Kennung</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography className={classes.mobileValue}>{row.helperKennung || '-'}</Typography>
                                {row.helperKennung && (
                                  <IconButton
                                    size='small'
                                    onClick={() => handleResendKennung(row.helperId)}
                                    color='primary'
                                    disabled={resendStatus[row.helperId] === 'loading' || !row.helperEmail}
                                    title={!row.helperEmail ? 'Keine E-Mail-Adresse vorhanden' : 'Kennung erneut versenden'}
                                  >
                                    {resendStatus[row.helperId] === 'loading' ? (
                                      <CircularProgress size={16} color='inherit' />
                                    ) : resendStatus[row.helperId] === 'success' ? (
                                      <CheckIcon fontSize='small' color='success' />
                                    ) : (
                                      <EmailIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Zeit</Typography>
                              <Typography className={classes.mobileValue}>
                                {formatTime(row.dayFrom)} - {formatTime(row.dayTo)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Check-in</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography className={classes.mobileValue}>{formatDateTime(row.checkinAt)}</Typography>
                                <IconButton size='small' onClick={() => handleOpenDialog(row, 'checkin')} sx={{ color: 'red', p: 0.5 }}>
                                  {row.checkinAt ? <AlarmIcon fontSize='small' /> : <AddAlarmIcon fontSize='small' />}
                                </IconButton>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.mobileLabel}>Check-out</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography className={classes.mobileValue}>{formatDateTime(row.checkoutAt)}</Typography>
                                <IconButton
                                  size='small'
                                  onClick={() => handleOpenDialog(row, 'checkout')}
                                  disabled={!row.checkinAt}
                                  sx={{ color: !row.checkinAt ? 'inherit' : 'red', p: 0.5 }}
                                >
                                  {row.checkoutAt ? <AlarmIcon fontSize='small' /> : <AddAlarmIcon fontSize='small' />}
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ))}
              </Box>
            ))}
            {filteredData.length === 0 && (
              <Typography align='center' sx={{ my: 4 }}>
                Keine Einsätze für den ausgewählten Tag gefunden.
              </Typography>
            )}
          </Box>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader aria-label='admin plan table'>
              <TableHead>
                <TableRow>
                  <TableCell><b>Helfer</b></TableCell>
                  <TableCell><b>Vereinsmitglied</b></TableCell>
                  <TableCell><b>Shirt erhalten</b></TableCell>
                  <TableCell><b>Kennung</b></TableCell>
                  <TableCell><b>Geplant von</b></TableCell>
                  <TableCell><b>Geplant bis</b></TableCell>
                  <TableCell><b>Check-in</b></TableCell>
                  <TableCell><b>Check-out</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(groupedData).map((areaKey) => (
                  <React.Fragment key={areaKey}>
                    <TableRow className={classes.areaHeader}>
                      <TableCell colSpan={8}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                          Bereich: {areaKey}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {Object.keys(groupedData[areaKey]).map((employmentKey) => (
                      <React.Fragment key={employmentKey}>
                        <TableRow className={classes.groupHeader}>
                          <TableCell colSpan={8} sx={{ pl: 4 }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                              {employmentKey}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {groupedData[areaKey][employmentKey].map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.helperName}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {row.isClubMember ? <CheckIcon color='success' /> : '-'}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Checkbox
                                checked={row.shirtNeeded}
                                onChange={() => handleToggleShirt(row.helperId)}
                                disabled={shirtLoading[row.helperId]}
                                size='small'
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {row.helperKennung || '-'}
                                {row.helperKennung && (
                                  <IconButton
                                    size='small'
                                    onClick={() => handleResendKennung(row.helperId)}
                                    title={!row.helperEmail ? 'Keine E-Mail-Adresse vorhanden' : 'Kennung erneut versenden'}
                                    color='primary'
                                    disabled={resendStatus[row.helperId] === 'loading' || !row.helperEmail}
                                  >
                                    {resendStatus[row.helperId] === 'loading' ? (
                                      <CircularProgress size={20} color='inherit' />
                                    ) : resendStatus[row.helperId] === 'success' ? (
                                      <CheckIcon fontSize='small' color='success' />
                                    ) : (
                                      <EmailIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{formatTime(row.dayFrom)}</TableCell>
                            <TableCell>{formatTime(row.dayTo)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {formatDateTime(row.checkinAt)}
                                <IconButton size='small' onClick={() => handleOpenDialog(row, 'checkin')} title='Manueller Check-in' sx={{ color: 'red' }}>
                                  {row.checkinAt ? <AlarmIcon fontSize='small' /> : <AddAlarmIcon fontSize='small' />}
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {formatDateTime(row.checkoutAt)}
                                <IconButton size='small' onClick={() => handleOpenDialog(row, 'checkout')} title='Manueller Check-out' disabled={!row.checkinAt} sx={{ color: !row.checkinAt ? 'inherit' : 'red' }}>
                                  {row.checkoutAt ? <AlarmIcon fontSize='small' /> : <AddAlarmIcon fontSize='small' />}
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align='center'>
                      Keine Einsätze für den ausgewählten Tag gefunden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Manual Check-in/out Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='xs' fullWidth>
          <DialogTitle>Manueller Check-in / Check-out</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='body1'>
                <b>Helfer:</b> {selectedRecord?.helperName}
              </Typography>
              <Typography variant='body1'>
                <b>Einsatz:</b> {selectedRecord?.employmentName} ({selectedRecord?.area.displayName})
              </Typography>
              <DateTimePicker
                label='Zeitpunkt'
                value={manualTime}
                onChange={(newValue) => setManualTime(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <Typography variant='body2' color='textSecondary'>
                Hinweis: Wenn bereits ein Check-in vorhanden ist, wird dieser aktualisiert.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleManualCheckin} variant='contained' color='primary'>
              Speichern
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  )
}

export default AdminPlan
